import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Briefcase, FileText, CheckCircle } from 'lucide-react';
import PageHero from '../components/PageHero';
import { fadeUp, stagger, viewportOpts } from '../hooks/useScrollAnimation';
import { openRoles } from '../data/careers';
import { siteConfig } from '../config/siteConfig';

const Wave = ({ fill, bg = "transparent" }) => (
  <div className="wave-divider" style={{ background: bg }}>
    <svg viewBox="0 0 1440 70" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ height: 50 }}>
      <path d="M0,35 C200,70 400,0 720,35 C1040,70 1280,5 1440,35 L1440,70 L0,70 Z" fill={fill} />
    </svg>
  </div>
);

const initialForm = {
  fullName: '',
  phone: '',
  email: '',
  position: '',
  qualification: '',
  experience: '',
  intro: '',
  resume: null,
  consent: false
};

export default function Careers() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Full name is required";
    if (!form.phone.trim() || !/^\+?[0-9\s-]{10,15}$/.test(form.phone)) e.phone = "Valid phone number is required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email is required";
    if (!form.position) e.position = "Please select a position";
    if (!form.qualification.trim()) e.qualification = "Qualification is required";
    if (!form.experience.trim()) e.experience = "Experience details are required";
    if (!form.consent) e.consent = "You must consent to data processing";
    return e;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, resume: "File size must be less than 5MB" });
      } else {
        setForm({ ...form, resume: file });
        setErrors({ ...errors, resume: '' });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    // Simulate submission / email flow since no secure upload exists
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1200);
  };

  const handleChange = (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [e.target.name]: val });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  return (
    <div>
      <PageHero 
        title="Build Meaningful Careers. Shape Bright Futures." 
        subtitle="We welcome passionate educators and caring professionals who enjoy working with young children and believe in joyful, respectful, and child-centered education." 
        breadcrumb="Careers" 
      />

      {/* Open Roles */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Join Our Team</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Current Openings</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">Explore our available positions or send an open application for future teaching or administrative roles.</p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-8 mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOpts}
            variants={stagger}
          >
            {openRoles.map((role, i) => (
              <motion.div 
                key={i} 
                variants={fadeUp} 
                className="bg-[#F8FAFC] rounded-3rem p-8 border border-gray-100 shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <span className="bg-[#E0F2FE] text-[#0284c7] text-xs font-bold px-3 py-1 rounded-full">{role.department}</span>
                    <span className="text-gray-400 text-xs font-semibold">{role.type}</span>
                  </div>
                  <h3 className="font-nunito font-extrabold text-2xl text-[#1A1A1A] mb-3">{role.title}</h3>
                  <div className="flex gap-4 text-sm text-gray-500 mb-4 font-poppins">
                    <span><strong>Experience:</strong> {role.experience}</span>
                    <span><strong>Qualifications:</strong> {role.qualification}</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">{role.desc}</p>
                </div>
                <div>
                  <a href="#apply-form" onClick={() => setForm({ ...form, position: role.id })} className="btn-primary !text-sm !px-5 !py-2.5">
                    Apply For Role
                  </a>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Open Application Option */}
          <div className="bg-[#F3F9E2] rounded-3rem p-8 border border-[#9DC41E]/20 text-center max-w-3xl mx-auto">
            <h3 className="font-nunito font-bold text-xl text-[#1A1A1A] mb-2">Don't see a matching position?</h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              We are always growing and looking for exceptional talents (teachers, support maids, administrators). Send an open application, and we'll keep you in mind for future vacancies.
            </p>
            <a href="#apply-form" onClick={() => setForm({ ...form, position: "open-application" })} className="btn-secondary !text-sm !px-5 !py-2.5">
              Submit Open Application
            </a>
          </div>
        </div>
      </section>

      <Wave fill="#F8FAFC" bg="white" />

      {/* Careers Form */}
      <section id="apply-form" className="py-20 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Application Form</span>
            <h2 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mt-3">Apply Today</h2>
          </motion.div>

          <div className="bg-white rounded-3rem p-8 md:p-12 shadow-lg border border-gray-100">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-20 h-20 bg-[#F3F9E2] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-[#9DC41E]" />
                </div>
                <h3 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mb-3">Application Received!</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Thank you for applying to {siteConfig.name}. If your qualifications match our requirements, our HR team will contact you.
                </p>
                <div className="bg-[#F8FAFC] rounded-2xl p-5 border border-gray-100 max-w-md mx-auto text-left text-sm text-gray-500">
                  <p className="font-semibold text-gray-700 mb-2">Alternative Submission:</p>
                  <p className="mb-2">You can also email your updated CV directly to our HR desk at:</p>
                  <a href={`mailto:${siteConfig.email}`} className="text-[#C8161D] font-bold block mb-1">{siteConfig.email}</a>
                  <p>Or chat with us on WhatsApp at <a href={`tel:${siteConfig.phoneRaw}`} className="text-[#9DC41E] font-bold">{siteConfig.phone}</a>.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Full Name *</label>
                    <input name="fullName" value={form.fullName} onChange={handleChange} className="input-field" placeholder="Your name" />
                    {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Phone Number *</label>
                    <input name="phone" value={form.phone} onChange={handleChange} className="input-field" placeholder="10-digit number" />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Email Address *</label>
                    <input name="email" type="email" value={form.email} onChange={handleChange} className="input-field" placeholder="your@email.com" />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Position Applied For *</label>
                    <select name="position" value={form.position} onChange={handleChange} className="input-field">
                      <option value="">-- Select Position --</option>
                      <option value="preschool-teacher">Preschool Teacher</option>
                      <option value="assistant-teacher">Assistant Teacher</option>
                      <option value="activity-teacher">Activity Teacher</option>
                      <option value="reception-admin">Reception &amp; Admin</option>
                      <option value="open-application">Open Application (Other)</option>
                    </select>
                    {errors.position && <p className="text-red-500 text-xs mt-1">{errors.position}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Qualifications *</label>
                    <input name="qualification" value={form.qualification} onChange={handleChange} className="input-field" placeholder="e.g. NTT, B.Ed, Graduate" />
                    {errors.qualification && <p className="text-red-500 text-xs mt-1">{errors.qualification}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Years of Experience *</label>
                    <input name="experience" value={form.experience} onChange={handleChange} className="input-field" placeholder="e.g. 3 Years, Fresher" />
                    {errors.experience && <p className="text-red-500 text-xs mt-1">{errors.experience}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Short Introduction</label>
                  <textarea name="intro" value={form.intro} onChange={handleChange} rows={3} className="input-field resize-none" placeholder="Briefly describe why you'd like to join us..." />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Resume / CV (PDF/Word/Image, Max 5MB) *</label>
                  <div className="flex items-center gap-4">
                    <input type="file" onChange={handleFileChange} accept=".pdf,.doc,.docx,image/*" className="hidden" id="resume-file" />
                    <label htmlFor="resume-file" className="btn-outline !text-xs !py-2.5 cursor-pointer">
                      Choose File
                    </label>
                    <span className="text-xs text-gray-500">
                      {form.resume ? form.resume.name : "No file selected"}
                    </span>
                  </div>
                  {errors.resume && <p className="text-red-500 text-xs mt-1">{errors.resume}</p>}
                </div>

                <div className="flex items-start gap-2.5">
                  <input type="checkbox" name="consent" checked={form.consent} onChange={handleChange} className="mt-1" id="consent-check" />
                  <label htmlFor="consent-check" className="text-xs text-gray-500 leading-relaxed">
                    I consent to Happykidz Preschool storing my resume details for recruitment evaluation.
                  </label>
                </div>
                {errors.consent && <p className="text-red-500 text-xs">{errors.consent}</p>}

                <button type="submit" disabled={loading} className="btn-primary w-full justify-center text-base">
                  {loading ? "Submitting Application..." : "Submit Application"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
